import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import Navbar from '../components/layout/Navbar';
import Footer from '../components/layout/Footer';
import PriceCalculatorAdvanced from '../components/PriceCalculatorAdvanced';
import InteractiveGlobe from '../components/InteractiveGlobe';
import DealsShowcase from '../components/DealsShowcase';
import { 
  Package, 
  Truck, 
  CreditCard, 
  Bell, 
  Globe, 
  ShieldCheck,
  ArrowRight,
  CheckCircle,
  Mail,
  Share2,
  Star,
  Quote,
  Ship,
  Car,
  Home,
  Building2
} from 'lucide-react';

const fadeInUp = {
  initial: { opacity: 0, y: 20 },
  animate: { opacity: 1, y: 0 },
  transition: { duration: 0.5 }
};

const staggerContainer = {
  animate: {
    transition: {
      staggerChildren: 0.1
    }
  }
};

export default function LandingPage() {
  const features = [
    {
      icon: Package,
      title: 'Réception en entrepôt',
      description: 'Recevez vos colis de n\'importe quelle boutique en ligne à notre adresse à Béziers.'
    },
    {
      icon: Bell,
      title: 'Notification par email',
      description: 'Dès réception de votre colis, vous êtes notifié par email automatiquement.'
    },
    {
      icon: CreditCard,
      title: 'Paiement sécurisé',
      description: 'Payez les frais d\'expédition en ligne par carte bancaire via Stripe.'
    },
    {
      icon: Truck,
      title: 'Réexpédition mondiale',
      description: 'DOM-TOM, Afrique, Europe, Asie... Nous réexpédions partout.'
    },
    {
      icon: Globe,
      title: 'Suivi en temps réel',
      description: 'Suivez votre colis à chaque étape grâce au lien de suivi Colissimo.'
    },
    {
      icon: ShieldCheck,
      title: 'Stockage sécurisé',
      description: 'Vos colis sont stockés en sécurité dans notre entrepôt à Béziers.'
    }
  ];

  const steps = [
    { step: '01', title: 'Créez votre compte', description: 'Obtenez votre numéro client unique (ex: CB-XXXXXX) et notre adresse d\'entrepôt à Béziers.' },
    { step: '02', title: 'Faites vos achats', description: 'Commandez sur Amazon, Shein, Zalando... Indiquez TOUJOURS votre numéro client sur le colis !' },
    { step: '03', title: 'On vous notifie', description: 'Dès réception, vous recevez un email avec le détail et le tarif d\'expédition.' },
    { step: '04', title: 'Payez & on expédie', description: 'Payez en ligne et nous réexpédions vers votre adresse de destination.' },
  ];

  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      
      {/* Hero Section */}
      <section className="relative bg-slate-900 pt-24 pb-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-tr from-blue-600/10 via-blue-500/5 to-transparent" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div 
              className="text-left"
              initial={{ opacity: 0, x: -30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <h1 className="font-heading font-black text-5xl md:text-7xl text-white leading-[0.9] tracking-tight mb-6">
                Vos colis,<br />
                <span className="text-blue-500">partout</span><br />
                dans le monde
              </h1>
              <p className="text-xl text-slate-300 mb-8 max-w-lg">
                Commandez en France, nous réexpédions vers votre pays. 
                Simple, rapide, sécurisé.
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link to="/register">
                  <Button 
                    size="lg" 
                    className="bg-blue-600 hover:bg-blue-700 text-white px-8 py-6 text-lg font-bold uppercase tracking-wider rounded-lg shadow-lg hover:shadow-xl transition-all"
                    data-testid="hero-cta-btn"
                  >
                    Créer mon compte
                    <ArrowRight className="w-5 h-5 ml-2" />
                  </Button>
                </Link>
                <Link to="/tracking">
                  <Button 
                    size="lg" 
                    variant="outline" 
                    className="border-2 border-white text-white hover:bg-white hover:text-slate-900 px-8 py-6 text-lg font-bold uppercase tracking-wider rounded-lg transition-all"
                    data-testid="hero-track-btn"
                  >
                    Suivre un colis
                  </Button>
                </Link>
              </div>
            </motion.div>
            
            <motion.div 
              className="hidden lg:block"
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.6, delay: 0.2 }}
            >
              <div className="relative">
                <div className="absolute -inset-4 bg-blue-500/20 rounded-3xl blur-2xl" />
                <img 
                  src="https://images.pexels.com/photos/4391470/pexels-photo-4391470.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940" 
                  alt="Delivery packages" 
                  className="relative rounded-2xl shadow-2xl w-full object-cover aspect-[4/3]"
                />
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Important Notice - Customer Number */}
      <section className="py-12 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="bg-white rounded-2xl p-8 shadow-xl">
            <div className="flex flex-col md:flex-row items-start gap-6">
              <div className="w-16 h-16 bg-blue-100 rounded-xl flex items-center justify-center flex-shrink-0">
                <Package className="w-8 h-8 text-blue-600" />
              </div>
              <div>
                <h3 className="font-heading font-bold text-2xl text-slate-900 mb-3">
                  Comment identifier vos colis ?
                </h3>
                <p className="text-slate-600 mb-4">
                  Lors de vos achats en ligne, faites livrer à notre entrepôt en indiquant :
                </p>
                <div className="bg-slate-50 border-2 border-dashed border-slate-300 rounded-lg p-4 font-mono text-sm">
                  <p><strong>Nom :</strong> <span className="text-blue-600 font-bold">ISMPARCEL + VOTRE NUMÉRO CLIENT</span></p>
                  <p><strong>Adresse :</strong> 105 Rue Joseph Marie Jacquard</p>
                  <p><strong>Code postal / Ville :</strong> 34500 Béziers</p>
                  <p><strong>Pays :</strong> France</p>
                </div>
                <p className="text-sm text-slate-500 mt-3">
                  <strong className="text-blue-600">Exemple :</strong> ISMPARCEL CB-ABC123 - Le numéro client suffit pour identifier votre colis !
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Interactive Globe — moved up just under the hero */}
      <section className="py-16 md:py-20 bg-gradient-to-b from-slate-100 via-blue-50/40 to-slate-100 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute top-10 left-1/4 w-96 h-96 bg-blue-300 rounded-full blur-3xl opacity-20" />
          <div className="absolute bottom-10 right-1/4 w-96 h-96 bg-cyan-300 rounded-full blur-3xl opacity-20" />
        </div>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center mb-8 md:mb-10">
            <span className="inline-flex items-center gap-2 bg-blue-600/10 text-blue-700 px-4 py-1.5 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
              <Globe className="w-3.5 h-3.5" />
              Nos destinations dans le monde
            </span>
            <h2 className="font-heading font-black text-3xl sm:text-4xl md:text-5xl lg:text-6xl text-slate-900 tracking-tight mb-4">
              Vos colis voyagent <span className="bg-gradient-to-r from-blue-600 to-cyan-500 bg-clip-text text-transparent">partout</span>
            </h2>
            <p className="text-base md:text-lg text-slate-600 max-w-2xl mx-auto">
              Touchez une destination pour découvrir les délais et tarifs en temps réel.
            </p>
          </div>
          <InteractiveGlobe />
        </div>
      </section>

      {/* Price Calculator Section */}
      <section className="py-16 bg-slate-100">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5 }}
            >
              <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 tracking-tight mb-4">
                Estimez le coût de votre envoi
              </h2>
              <p className="text-lg text-slate-600 mb-6">
                Utilisez notre calculateur pour obtenir une estimation instantanée 
                du prix d'expédition vers votre destination.
              </p>
              <ul className="space-y-3">
                {[
                  'Tarifs officiels La Poste/Colissimo',
                  'Prix tout compris, pas de frais cachés',
                  'Suivi inclus dans tous nos envois'
                ].map((item, i) => (
                  <li key={i} className="flex items-center gap-3">
                    <CheckCircle className="w-5 h-5 text-green-500 flex-shrink-0" />
                    <span className="text-slate-700">{item}</span>
                  </li>
                ))}
              </ul>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: 0.2 }}
            >
              <PriceCalculatorAdvanced />
            </motion.div>
          </div>
        </div>
      </section>

      {/* How it works */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-slate-900 tracking-tight mb-4">
              Comment ça marche ?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              En 4 étapes simples, recevez vos achats en ligne où que vous soyez.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {steps.map((item, index) => (
              <motion.div 
                key={index}
                variants={fadeInUp}
              >
                <Card className="h-full bg-white border border-slate-200 shadow-sm hover:shadow-md transition-shadow duration-300 rounded-xl overflow-hidden group">
                  <CardContent className="p-6">
                    <div className="text-6xl font-heading font-black text-blue-500/20 group-hover:text-blue-500/40 transition-colors mb-4">
                      {item.step}
                    </div>
                    <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">
                      {item.title}
                    </h3>
                    <p className="text-slate-600">
                      {item.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Features */}
      <section className="py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            {...fadeInUp}
          >
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-slate-900 tracking-tight mb-4">
              Pourquoi ISMParcel ?
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Une solution complète pour tous vos besoins d'expédition internationale.
            </p>
          </motion.div>

          <motion.div 
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {features.map((feature, index) => (
              <motion.div 
                key={index}
                variants={fadeInUp}
                className="group"
              >
                <Card className="h-full bg-white border border-slate-100 shadow-sm hover:shadow-lg transition-all duration-300 rounded-2xl overflow-hidden hover:-translate-y-1">
                  <CardContent className="p-6">
                    <div className="w-14 h-14 bg-blue-100 rounded-xl flex items-center justify-center mb-4 group-hover:bg-blue-600 transition-colors">
                      <feature.icon className="w-7 h-7 text-blue-600 group-hover:text-white transition-colors" />
                    </div>
                    <h3 className="font-heading font-bold text-xl text-slate-900 mb-2">
                      {feature.title}
                    </h3>
                    <p className="text-slate-600">
                      {feature.description}
                    </p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>

      {/* Fret Banner - Cross-sell */}
      <section className="relative py-24 bg-gradient-to-br from-slate-900 via-blue-950 to-slate-900 text-white overflow-hidden" data-testid="fret-banner">
        <div className="absolute inset-0 opacity-20" style={{ backgroundImage: 'radial-gradient(circle at 15% 50%, #3b82f6 0%, transparent 45%), radial-gradient(circle at 85% 70%, #f59e0b 0%, transparent 45%)' }} />
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
            className="text-center max-w-4xl mx-auto mb-14"
          >
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-amber-500/20 border border-amber-400/40 text-amber-200 rounded-full text-sm font-bold mb-6">
              <Ship className="w-4 h-4" />
              NOUVEAU — ISMParcel Fret & Logistique
            </div>
            <h2 className="font-heading font-bold text-4xl md:text-5xl lg:text-6xl tracking-tight mb-5 leading-tight">
              Envoyez <span className="bg-gradient-to-r from-amber-400 to-orange-400 bg-clip-text text-transparent">absolument tout</span>,<br />
              partout dans le monde
            </h2>
            <p className="text-lg md:text-xl text-slate-300 mb-3 leading-relaxed">
              Pour <strong className="text-white">particuliers et professionnels</strong> — voitures, motos, palettes,
              déménagements, conteneurs complets, fret commercial...
            </p>
            <p className="text-base text-slate-400">
              Enlèvement partout en France · Transport maritime · Dédouanement · Livraison finale
            </p>
          </motion.div>

          {/* 4 services */}
          <motion.div
            className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-10"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            {[
              { icon: Car, label: 'Véhicules', desc: 'Voitures, motos, quads', color: 'from-blue-500 to-cyan-500' },
              { icon: Package, label: 'Palettes', desc: '1 à 50 palettes', color: 'from-emerald-500 to-teal-500' },
              { icon: Home, label: 'Déménagement', desc: 'Conteneur 20\' / 40\'', color: 'from-purple-500 to-indigo-500' },
              { icon: Building2, label: 'Fret Pro', desc: 'Grossistes, e-commerce', color: 'from-orange-500 to-amber-500' }
            ].map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={i} className="bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-5 hover:bg-white/10 hover:border-white/20 transition-all hover:-translate-y-1">
                  <div className={`w-12 h-12 rounded-xl bg-gradient-to-br ${s.color} flex items-center justify-center mb-3`}>
                    <Icon className="w-6 h-6 text-white" />
                  </div>
                  <h4 className="font-heading font-bold text-white text-lg mb-1">{s.label}</h4>
                  <p className="text-sm text-slate-400">{s.desc}</p>
                </div>
              );
            })}
          </motion.div>

          {/* Trust row + CTA */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 0.2 }}
            className="flex flex-col md:flex-row items-center justify-between gap-6 bg-white/5 backdrop-blur-sm border border-white/10 rounded-2xl p-6"
          >
            <div className="flex flex-wrap items-center justify-center md:justify-start gap-6 text-sm text-slate-300">
              <div className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-emerald-400" /> Devis gratuit 24h</div>
              <div className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-emerald-400" /> Assurance cargo</div>
              <div className="flex items-center gap-2"><CheckCircle className="w-5 h-5 text-emerald-400" /> Sans engagement</div>
            </div>
            <Link to="/fret" data-testid="fret-banner-cta">
              <Button size="lg" className="bg-gradient-to-r from-amber-500 to-orange-500 hover:from-amber-600 hover:to-orange-600 text-white font-bold px-8 py-6 text-base shadow-xl shadow-amber-500/25">
                Demander un devis fret
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
          </motion.div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gradient-to-b from-slate-50 to-white" data-testid="testimonials-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div className="text-center mb-14" {...fadeInUp}>
            <div className="inline-flex items-center gap-2 px-4 py-1.5 bg-blue-50 text-blue-700 rounded-full text-sm font-semibold mb-4">
              <Star className="w-4 h-4 fill-blue-600 text-blue-600" />
              4,9/5 — Plus de 100 clients satisfaits
            </div>
            <h2 className="font-heading font-bold text-4xl md:text-5xl text-slate-900 tracking-tight mb-4">
              Ils nous font confiance
            </h2>
            <p className="text-lg text-slate-600 max-w-2xl mx-auto">
              Des centaines de colis livrés chaque mois aux quatre coins du monde.
            </p>
          </motion.div>

          <motion.div
            className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6"
            variants={staggerContainer}
            initial="initial"
            whileInView="animate"
            viewport={{ once: true }}
          >
            {[
              {
                name: 'Manea T.',
                location: 'Papeete, Polynésie française',
                flag: '🇵🇫',
                rating: 5,
                text: "Je commande régulièrement sur Amazon et Shein. ISMParcel regroupe mes colis et m'évite des frais énormes. Service au top, je recommande !",
                initials: 'MT',
                color: 'from-blue-500 to-cyan-500'
              },
              {
                name: 'Karim B.',
                location: 'Casablanca, Maroc',
                flag: '🇲🇦',
                rating: 5,
                text: "Très réactif, communication par email parfaite. Mes colis arrivent toujours bien emballés. Bien moins cher que les autres prestataires.",
                initials: 'KB',
                color: 'from-emerald-500 to-teal-500'
              },
              {
                name: 'Sophie L.',
                location: 'Pointe-à-Pitre, Guadeloupe',
                flag: '🇬🇵',
                rating: 5,
                text: "Enfin une vraie alternative pour faire ses achats en métropole ! Numéro client simple, suivi en temps réel et paiement sécurisé. Bravo.",
                initials: 'SL',
                color: 'from-purple-500 to-indigo-500'
              },
              {
                name: 'Aïssatou D.',
                location: 'Dakar, Sénégal',
                flag: '🇸🇳',
                rating: 5,
                text: "Le simulateur de prix est très précis. J'ai reçu mon colis en 12 jours via Colissimo. L'équipe est sérieuse et professionnelle.",
                initials: 'AD',
                color: 'from-orange-500 to-red-500'
              },
              {
                name: 'Jean-Marc R.',
                location: 'Saint-Denis, La Réunion',
                flag: '🇷🇪',
                rating: 5,
                text: "J'utilise ISMParcel depuis 6 mois pour mes commandes Decathlon et Boulanger. Tarifs transparents, frais de gestion clairs. Aucune mauvaise surprise.",
                initials: 'JM',
                color: 'from-pink-500 to-rose-500'
              },
              {
                name: 'Fatou C.',
                location: 'Abidjan, Côte d\'Ivoire',
                flag: '🇨🇮',
                rating: 5,
                text: "Service client en français, très à l'écoute. La déclaration douanière en ligne m'a fait gagner un temps fou. Je recommande à 100%.",
                initials: 'FC',
                color: 'from-amber-500 to-yellow-500'
              }
            ].map((review, index) => (
              <motion.div
                key={index}
                variants={fadeInUp}
                data-testid={`testimonial-card-${index}`}
              >
                <Card className="h-full bg-white border border-slate-200 shadow-sm hover:shadow-xl transition-all duration-300 rounded-2xl overflow-hidden hover:-translate-y-1 relative">
                  <CardContent className="p-6">
                    <Quote className="w-10 h-10 text-blue-100 absolute top-4 right-4" />
                    <div className="flex items-center gap-1 mb-4">
                      {Array.from({ length: review.rating }).map((_, i) => (
                        <Star key={i} className="w-4 h-4 fill-amber-400 text-amber-400" />
                      ))}
                    </div>
                    <p className="text-slate-700 leading-relaxed mb-6 relative z-10">
                      "{review.text}"
                    </p>
                    <div className="flex items-center gap-3 pt-4 border-t border-slate-100">
                      <div className={`w-11 h-11 rounded-full bg-gradient-to-br ${review.color} flex items-center justify-center text-white font-bold text-sm flex-shrink-0`}>
                        {review.initials}
                      </div>
                      <div className="min-w-0">
                        <p className="font-semibold text-slate-900 truncate">{review.name}</p>
                        <p className="text-sm text-slate-500 truncate">
                          <span className="mr-1">{review.flag}</span>
                          {review.location}
                        </p>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </motion.div>

          {/* Trust badges */}
          <motion.div
            className="mt-14 flex flex-wrap justify-center items-center gap-8 md:gap-12"
            {...fadeInUp}
          >
            <div className="flex items-center gap-2 text-slate-600">
              <ShieldCheck className="w-5 h-5 text-emerald-600" />
              <span className="text-sm font-medium">Paiement 100% sécurisé Stripe</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Package className="w-5 h-5 text-blue-600" />
              <span className="text-sm font-medium">Plus de 500 colis livrés</span>
            </div>
            <div className="flex items-center gap-2 text-slate-600">
              <Globe className="w-5 h-5 text-purple-600" />
              <span className="text-sm font-medium">Livraison dans 50+ pays</span>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Process Flow */}
      <section className="py-20 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading font-bold text-4xl md:text-5xl tracking-tight mb-4">
              Le processus en détail
            </h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div className="text-center p-6">
              <div className="w-20 h-20 bg-blue-600 rounded-full flex items-center justify-center mx-auto mb-4">
                <Package className="w-10 h-10 text-white" />
              </div>
              <h3 className="font-heading font-bold text-xl mb-2">1. Réception</h3>
              <p className="text-slate-400">
                Votre colis arrive à notre entrepôt. Nous l'identifions grâce à votre numéro client et l'enregistrons.
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-20 h-20 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Mail className="w-10 h-10 text-white" />
              </div>
              <h3 className="font-heading font-bold text-xl mb-2">2. Notification</h3>
              <p className="text-slate-400">
                Vous recevez un email avec les détails : poids, dimensions et tarif. Vous payez en ligne quand vous êtes prêt.
              </p>
            </div>
            
            <div className="text-center p-6">
              <div className="w-20 h-20 bg-purple-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Truck className="w-10 h-10 text-white" />
              </div>
              <h3 className="font-heading font-bold text-xl mb-2">3. Réexpédition</h3>
              <p className="text-slate-400">
                Dès paiement reçu, nous expédions vers votre adresse. Vous recevez un lien de suivi par email.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ISM Deals showcase */}
      <DealsShowcase />

      {/* Subscription teaser */}
      <section className="py-16 md:py-20 bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900 relative overflow-hidden">
        <div className="absolute inset-0 pointer-events-none opacity-30" style={{
          backgroundImage: 'radial-gradient(circle at 20% 30%, rgba(59,130,246,0.5), transparent 40%), radial-gradient(circle at 80% 60%, rgba(147,51,234,0.4), transparent 40%)',
        }} />
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="grid md:grid-cols-2 gap-10 items-center">
            <div className="text-white">
              <span className="inline-flex items-center gap-2 bg-amber-500/20 text-amber-300 border border-amber-400/30 px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider mb-4">
                ⭐ Nouveau — Abonnement mensuel
              </span>
              <h2 className="font-heading font-black text-3xl md:text-4xl lg:text-5xl leading-tight mb-4">
                Envoyez plus, <span className="bg-gradient-to-r from-amber-300 to-orange-400 bg-clip-text text-transparent">payez moins</span>
              </h2>
              <p className="text-white/80 text-base md:text-lg mb-6 leading-relaxed">
                Vous envoyez régulièrement des colis ? Nos formules mensuelles vous font économiser les frais de gestion — vous ne payez que l'expédition. À partir de <strong className="text-amber-300">19,99€/mois</strong>.
              </p>
              <ul className="space-y-2 mb-8">
                {[
                  '5, 10 ou 50 colis inclus par mois',
                  'Frais de gestion 100% offerts sur les colis inclus',
                  'Priorité entrepôt + stockage étendu',
                  'Sans engagement, annulable à tout moment',
                ].map((f, i) => (
                  <li key={i} className="flex items-center gap-3 text-white/90">
                    <span className="w-6 h-6 rounded-full bg-emerald-500/20 border border-emerald-400/30 flex items-center justify-center flex-shrink-0">
                      <svg className="w-3.5 h-3.5 text-emerald-300" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                    </span>
                    {f}
                  </li>
                ))}
              </ul>
              <Link to="/abonnements">
                <Button className="bg-white text-blue-900 hover:bg-amber-300 hover:text-slate-900 font-bold h-12 px-8 text-base shadow-2xl group" data-testid="landing-cta-sub">
                  Découvrir les formules
                  <ArrowRight className="w-4 h-4 ml-2 group-hover:translate-x-1 transition-transform" />
                </Button>
              </Link>
            </div>
            {/* Preview 3 cards */}
            <div className="grid grid-cols-3 gap-2 md:gap-3">
              {[
                { name: 'Starter', price: '19,99', qty: '5', accent: 'from-amber-400 to-orange-400' },
                { name: 'Pro', price: '39,99', qty: '10', accent: 'from-blue-400 to-cyan-400', highlight: true },
                { name: 'Business', price: '79,99', qty: '50', accent: 'from-purple-400 to-fuchsia-400' },
              ].map((p) => (
                <div key={p.name} className={`relative bg-white/10 backdrop-blur border border-white/20 rounded-2xl p-3 md:p-4 text-white text-center ${p.highlight ? 'scale-105 shadow-2xl' : ''}`}>
                  {p.highlight && <div className="absolute -top-2 left-1/2 -translate-x-1/2 text-[10px] font-bold bg-amber-400 text-slate-900 px-2 py-0.5 rounded-full">POP</div>}
                  <p className={`font-heading font-bold text-sm mb-1 bg-gradient-to-r ${p.accent} bg-clip-text text-transparent`}>{p.name}</p>
                  <p className="font-heading font-black text-lg md:text-2xl">{p.price}€</p>
                  <p className="text-[10px] md:text-xs text-white/60 mt-0.5">/mois</p>
                  <div className="h-px bg-white/20 my-2" />
                  <p className="text-xs md:text-sm font-bold">{p.qty} colis</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Prohibited Items */}
      <section className="py-16 bg-red-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <h2 className="font-heading font-bold text-3xl md:text-4xl text-slate-900 tracking-tight mb-4">
              Produits interdits à l'expédition
            </h2>
            <p className="text-lg text-slate-600">
              Pour des raisons de sécurité et de réglementation, les articles suivants ne peuvent pas être expédiés.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[
              { icon: "🍔", title: "Nourriture & Denrées périssables", desc: "Aliments, boissons, produits frais" },
              { icon: "🔋", title: "Batteries & Piles", desc: "Lithium, piles, accumulateurs" },
              { icon: "💊", title: "Médicaments & Drogues", desc: "Substances illicites, médicaments non autorisés" },
              { icon: "🔥", title: "Produits inflammables", desc: "Essence, alcool, aérosols, gaz" },
              { icon: "💣", title: "Armes & Explosifs", desc: "Armes à feu, munitions, feux d'artifice" },
              { icon: "☠️", title: "Produits toxiques", desc: "Produits chimiques, poisons, acides" },
              { icon: "🚬", title: "Tabac & Cigarettes", desc: "Cigarettes, tabac, e-liquides" },
              { icon: "💰", title: "Argent & Valeurs", desc: "Espèces, chèques, cartes bancaires" },
              { icon: "📦", title: "Contrefaçons", desc: "Produits de marques contrefaits" },
            ].map((item, index) => (
              <div key={index} className="flex items-start gap-3 p-4 bg-white rounded-lg border border-red-200">
                <span className="text-2xl">{item.icon}</span>
                <div>
                  <h3 className="font-bold text-slate-900">{item.title}</h3>
                  <p className="text-sm text-slate-600">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
          
          <p className="text-center text-sm text-slate-500 mt-8">
            Cette liste n'est pas exhaustive. En cas de doute, contactez-nous avant d'envoyer votre colis.
          </p>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-blue-600">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading font-bold text-4xl md:text-5xl text-white tracking-tight mb-6">
            Prêt à expédier ?
          </h2>
          <p className="text-xl text-white/90 mb-8">
            Créez votre compte gratuitement et commencez à recevoir vos colis dès aujourd'hui.
          </p>
          <Link to="/register">
            <Button 
              size="lg" 
              className="bg-white hover:bg-slate-100 text-blue-600 px-10 py-6 text-lg font-bold uppercase tracking-wider rounded-lg shadow-lg hover:shadow-xl transition-all"
              data-testid="cta-register-btn"
            >
              Créer mon compte gratuit
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </Link>
        </div>
      </section>

      {/* Share Section */}
      <section className="py-12 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Share2 className="w-5 h-5 text-slate-600" />
            <h3 className="font-heading font-bold text-lg text-slate-700">
              Partagez ISMParcel avec vos proches
            </h3>
          </div>
          <p className="text-sm text-slate-500 mb-6">
            Faites découvrir notre service à vos amis et famille !
          </p>
          <div className="flex flex-wrap justify-center gap-3">
            {/* Facebook */}
            <a
              href="https://www.facebook.com/sharer/sharer.php?u=https://ismparcel.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2.5 bg-[#1877F2] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm"
              data-testid="share-facebook"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              Facebook
            </a>
            
            {/* WhatsApp */}
            <a
              href="https://wa.me/?text=Découvrez%20ISMParcel%20pour%20expédier%20vos%20colis%20vers%20les%20DOM-TOM%20et%20l'international%20!%20https://ismparcel.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2.5 bg-[#25D366] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm"
              data-testid="share-whatsapp"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z"/></svg>
              WhatsApp
            </a>
            
            {/* Twitter/X */}
            <a
              href="https://twitter.com/intent/tweet?text=Découvrez%20ISMParcel%20pour%20expédier%20vos%20colis%20vers%20les%20DOM-TOM%20et%20l'international%20!&url=https://ismparcel.fr"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2.5 bg-black text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm"
              data-testid="share-twitter"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/></svg>
              X (Twitter)
            </a>
            
            {/* LinkedIn */}
            <a
              href="https://www.linkedin.com/shareArticle?mini=true&url=https://ismparcel.fr&title=ISMParcel%20-%20Expédition%20de%20colis&summary=Service%20d'expédition%20vers%20les%20DOM-TOM%20et%20l'international"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-2 px-4 py-2.5 bg-[#0A66C2] text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm"
              data-testid="share-linkedin"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 24 24"><path d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037-1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286zM5.337 7.433c-1.144 0-2.063-.926-2.063-2.065 0-1.138.92-2.063 2.063-2.063 1.14 0 2.064.925 2.064 2.063 0 1.139-.925 2.065-2.064 2.065zm1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0 1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24 22.271V1.729C24 .774 23.2 0 22.222 0h.003z"/></svg>
              LinkedIn
            </a>
            
            {/* Email */}
            <a
              href="mailto:?subject=Découvrez ISMParcel&body=Bonjour,%0A%0AJe te recommande ISMParcel pour expédier tes colis vers les DOM-TOM et l'international.%0A%0Ahttps://ismparcel.fr"
              className="flex items-center gap-2 px-4 py-2.5 bg-slate-600 text-white rounded-lg hover:opacity-90 transition-opacity font-medium text-sm"
              data-testid="share-email"
            >
              <Mail className="w-5 h-5" />
              Email
            </a>
            
            {/* Copy Link */}
            <button
              onClick={() => {
                navigator.clipboard.writeText('https://ismparcel.fr');
                alert('Lien copié !');
              }}
              className="flex items-center gap-2 px-4 py-2.5 bg-slate-200 text-slate-700 rounded-lg hover:bg-slate-300 transition-colors font-medium text-sm"
              data-testid="share-copy"
            >
              <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
              Copier le lien
            </button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
